import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import useClickOutside from "../lib/useClickOutside";
import { useAuth } from "../context/AuthContext";
import { useUpgradeModal } from "../context/UpgradeModalContext";
import {
  CurrencyDollar,
  TrendUp,
  TrendDown,
  Sliders,
  Package,
  Stack,
  Calendar,
  DownloadSimple,
  FileCsv,
  FilePdf,
  ChartBar,
  Lock,
} from "@phosphor-icons/react";

const KPI = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-zinc-300 p-4 sm:p-6">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="font-mono font-bold text-lg sm:text-2xl lg:text-3xl mt-2 sm:mt-3 text-zinc-900 truncate">{value}</div>
        {sub && <div className="text-[10px] sm:text-xs text-zinc-500 mt-1 line-clamp-2">{sub}</div>}
      </div>
      <div className={`h-8 w-8 sm:h-10 sm:w-10 ${accent} flex items-center justify-center flex-shrink-0`}>
        <Icon size={16} weight="duotone" className="sm:hidden" />
        <Icon size={20} weight="duotone" className="hidden sm:block" />
      </div>
    </div>
  </div>
);

const PRESETS = [
  { v: "7", label: "7 days" },
  { v: "30", label: "30 days" },
  { v: "90", label: "90 days" },
  { v: "365", label: "1 year" },
  { v: "all", label: "All time" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const { user } = useAuth();
  const { openUpgradeModal } = useUpgradeModal();
  const isBasic = !!user?.plan?.is_basic;
  const currentTier = user?.plan?.tier || "basic";
  const [preset, setPreset] = useState("30");
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  useClickOutside(exportRef, () => setExportOpen(false), exportOpen);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "all") {
      setStart("");
      setEnd("");
    } else {
      setStart(isoDaysAgo(Number(p)));
      setEnd(todayIso());
    }
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    try {
      const { data } = await api.get("/reports/financial", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isBasic) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line
  }, [start, end, isBasic]);

  const downloadExport = async (format) => {
    setExportOpen(false);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ format });
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
      const res = await fetch(`${API}/exports/financial?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financial-report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const dailyMax = useMemo(() => {
    if (!data?.daily?.length) return 1;
    return Math.max(...data.daily.map((d) => Math.max(d.in_value, d.out_value)), 1);
  }, [data]);

  // Basic-plan users don't have access to Financial Reports
  if (isBasic) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-zinc-300 p-8 sm:p-12 max-w-2xl mx-auto text-center" data-testid="reports-upgrade-notice">
          <div className="h-14 w-14 bg-[#1A2332] text-[#FF9800] rounded-full flex items-center justify-center mx-auto">
            <Lock size={24} weight="duotone" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-5 text-[#0F172A]">
            Financial reports
          </h1>
          <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
            Not included in the Basic plan
          </div>
          <p className="mt-5 text-sm sm:text-base text-zinc-600 leading-relaxed">
            Cost-of-materials reports, by-category breakdowns, and CSV/PDF financial exports are available on the <span className="font-semibold text-[#0F172A]">Pro</span> plan. Switch plans in seconds — Stripe handles the proration automatically.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() =>
                openUpgradeModal({
                  reason: "feature_not_in_plan",
                  message: "Financial reports are a Pro plan feature.",
                  currentTier,
                })
              }
              data-testid="reports-upgrade-button"
              className="inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold text-xs uppercase tracking-widest px-5 py-3 transition-colors"
            >
              Upgrade plan
            </button>
            <Link
              to="/dashboard"
              data-testid="reports-back-to-dashboard"
              className="inline-flex items-center justify-center gap-2 border border-zinc-300 hover:bg-zinc-100 text-zinc-700 font-bold text-xs uppercase tracking-widest px-5 py-3 transition-colors"
            >
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="p-8 text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading…</div>
    );
  }

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">
            Finance · Cost of materials
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Financial Reports
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {data.transaction_count.toLocaleString()} transactions
            {start && end ? ` from ${start} to ${end}` : start ? ` from ${start}` : end ? ` until ${end}` : " across all time"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-px bg-zinc-300">
            {PRESETS.map((p) => (
              <button
                key={p.v}
                onClick={() => applyPreset(p.v)}
                data-testid={`preset-${p.v}`}
                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${
                  preset === p.v ? "bg-[#1A2332] text-white" : "bg-white hover:bg-zinc-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setExportOpen((v) => !v)}
              data-testid="reports-export-button"
              className="inline-flex items-center gap-2 border border-zinc-300 bg-white hover:bg-zinc-100 px-4 py-2 text-sm font-semibold"
            >
              <DownloadSimple size={14} weight="bold" />
              Export
            </button>
            {exportOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-zinc-300 shadow-lg z-30 w-44">
                <button
                  onClick={() => downloadExport("csv")}
                  data-testid="reports-export-csv"
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm"
                >
                  <FileCsv size={16} weight="duotone" /> CSV
                </button>
                <button
                  onClick={() => downloadExport("pdf")}
                  data-testid="reports-export-pdf"
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-zinc-100 text-sm border-t border-zinc-200"
                >
                  <FilePdf size={16} weight="duotone" /> PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom date pickers */}
      <div className="bg-white border border-zinc-300 p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700 flex items-center gap-1">
            <Calendar size={10} weight="bold" /> Start date
          </label>
          <input
            type="date"
            value={start}
            onChange={(e) => { setPreset("custom"); setStart(e.target.value); }}
            data-testid="report-start-date"
            className="mt-1.5 px-3 py-2 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700 flex items-center gap-1">
            <Calendar size={10} weight="bold" /> End date
          </label>
          <input
            type="date"
            value={end}
            onChange={(e) => { setPreset("custom"); setEnd(e.target.value); }}
            data-testid="report-end-date"
            className="mt-1.5 px-3 py-2 border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
          />
        </div>
        {(start || end) && (
          <button
            onClick={() => { setStart(""); setEnd(""); setPreset("all"); }}
            className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 ml-auto"
            data-testid="report-clear-dates"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* KPI grid — 2 cols on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-300">
        <div className="bg-white">
          <KPI
            icon={TrendUp}
            label="Cost of materials in"
            value={fmt(data.in.value)}
            sub={`${data.in.units.toLocaleString()} units · purchases & receipts`}
            accent="bg-emerald-600 text-white"
          />
        </div>
        <div className="bg-white">
          <KPI
            icon={TrendDown}
            label="Cost of materials out"
            value={fmt(data.out.value)}
            sub={`${data.out.units.toLocaleString()} units · sales, production, damage`}
            accent="bg-red-600 text-white"
          />
        </div>
        <div className="bg-white">
          <KPI
            icon={Sliders}
            label="Net movement"
            value={fmt(data.net_movement_value)}
            sub={data.net_movement_value >= 0 ? "Inventory grew in value" : "Inventory shrunk in value"}
            accent="bg-[#FF9800] text-white"
          />
        </div>
        <div className="bg-white">
          <KPI
            icon={CurrencyDollar}
            label="Inventory on hand"
            value={fmt(data.current_inventory_value)}
            sub={`${data.active_parts_count} active SKUs · ${data.deleted_parts_count} archived`}
            accent="bg-[#1A2332] text-white"
          />
        </div>
      </div>

      {/* Daily chart */}
      <div className="bg-white border border-zinc-300">
        <div className="border-b border-zinc-300 px-6 py-4 flex items-center gap-2">
          <ChartBar size={18} weight="duotone" className="text-[#FF9800]" />
          <h2 className="font-display text-lg font-semibold">Daily cost flow</h2>
        </div>
        <div className="p-6">
          {data.daily.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-8">No transactions in this window.</div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex items-end gap-2 min-w-max h-48 pb-2 border-b-2 border-zinc-900">
                {data.daily.map((d) => {
                  const inH = (d.in_value / dailyMax) * 100;
                  const outH = (d.out_value / dailyMax) * 100;
                  return (
                    <div key={d.date} className="flex flex-col items-center gap-1" data-testid={`bar-${d.date}`}>
                      <div className="flex items-end gap-px h-44">
                        <div className="w-3 bg-emerald-600" style={{ height: `${inH}%` }} title={`IN ${fmt(d.in_value)}`} />
                        <div className="w-3 bg-red-600" style={{ height: `${outH}%` }} title={`OUT ${fmt(d.out_value)}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-600 inline-block" /> Cost in</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-600 inline-block" /> Cost out</span>
                <span className="ml-auto font-mono normal-case tracking-normal text-zinc-400">
                  {data.daily[0]?.date} → {data.daily[data.daily.length - 1]?.date}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Two-column breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* By reason */}
        <div className="bg-white border border-zinc-300 overflow-x-auto">
          <div className="border-b border-zinc-300 px-4 sm:px-6 py-4 flex items-center gap-2">
            <Package size={18} weight="duotone" className="text-[#FF9800]" />
            <h2 className="font-display text-base sm:text-lg font-semibold">By reason</h2>
          </div>
          {data.by_reason.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center">No data.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Reason</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">In</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Out</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">#</th>
                </tr>
              </thead>
              <tbody>
                {data.by_reason.map((r) => (
                  <tr key={r.reason} className="row-hover" data-testid={`reason-${r.reason}`}>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 capitalize">{r.reason}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-emerald-700 text-xs sm:text-sm">
                      {fmt(r.in_value)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-red-700 text-xs sm:text-sm">
                      {fmt(r.out_value)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-zinc-600">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* By category */}
        <div className="bg-white border border-zinc-300 overflow-x-auto">
          <div className="border-b border-zinc-300 px-4 sm:px-6 py-4 flex items-center gap-2">
            <Stack size={18} weight="duotone" className="text-[#FF9800]" />
            <h2 className="font-display text-base sm:text-lg font-semibold">By category</h2>
          </div>
          {data.by_category.length === 0 ? (
            <div className="p-8 text-sm text-zinc-500 text-center">No data.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Category</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">In</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Out</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.by_category.map((c) => (
                  <tr key={c.category} className="row-hover" data-testid={`cat-${c.category.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200">{c.category}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-emerald-700 text-xs sm:text-sm">
                      {fmt(c.in_value)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-red-700 text-xs sm:text-sm">
                      {fmt(c.out_value)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono font-bold text-xs sm:text-sm">
                      {fmt(c.in_value - c.out_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
